import DataGrid, {
    Column,
    Export,
    HeaderFilter,
    Paging,
    Scrolling,
    SearchPanel,
    Selection,
} from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';
import { LoadPanel } from 'devextreme-react/load-panel';
import { SelectBox } from 'devextreme-react/select-box';
import React, { Component } from 'react';
import Swal from 'sweetalert2';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';

import { DateFormat } from '../../helpers/fixcodes';
import { formatFromDate, formatToDate, getRequestParams } from '../../helpers/globalfunctions';
import DispatchFilterBar from './DispatchFilterBar';

const position = { of: '#dispatch-page' };

// Builds the 'yyyy-MM-dd' date part the guard-rides API expects, independent of the
// 'MM/dd/yyyy hh:mm' display format used by the date pickers/state.
function toApiDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return [year, month, day].join('-');
}

// Matches the MM/dd/yyyy HH:mm shape of DateFormat.DateTime24Format, for the stacked
// cellRender columns below where DevExtreme's own dataType="datetime" formatting doesn't apply.
function formatCellDateTime(value) {
    if (!value) {
        return '';
    }
    const d = new Date(value);
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const PENDING_STATUS_IDS = [101, 103, 105];
// Maximum number of rides a dispatcher can select (and assign a guard to) at once.
const MAX_SELECTED_RIDES = 1;
const IN_PROCESS_STATUS_IDS = [102, 104, 107, 108, 114];
const COMPLETED_STATUS_IDS = [116, 109, 110];
const CANCELLED_STATUS_IDS = [111, 118];

// Row status color class (styled globally in customstyle.css, same classes DispatchComponent uses).
// Pending rows get no color unless a guard is already assigned, in which case they're treated
// like In-Process; In-Process/Completed/Cancelled always do.
function getRowStatusClass(ride) {
    const statusId = parseInt(ride.tripStatusId);
    if (COMPLETED_STATUS_IDS.includes(statusId)) {
        return 'bgcompleted';
    }
    if (CANCELLED_STATUS_IDS.includes(statusId)) {
        return 'bgcancelled';
    }
    if (IN_PROCESS_STATUS_IDS.includes(statusId) || (PENDING_STATUS_IDS.includes(statusId) && ride.guardUserId)) {
        return 'bgassigned';
    }
    return null;
}

// Main text color for rows whose status background is too dark for the grid's default black
// text. Applied per cell in onCellPrepared because global grid CSS sets the color on each <td>,
// which overrides the row-level color in customstyle.css.
const ROW_TEXT_COLORS = {
    bgcompleted: '#ffffff',
};

// Secondary-line text color for the stacked multi-value cells, picked per row background
// so the sub-lines stay readable (the default grey disappears on the green Completed row).
const SECONDARY_TEXT_COLORS = {
    bgcompleted: 'rgba(255, 255, 255, 0.85)',
    bgcancelled: '#a0525a',
    bgassigned: '#495057',
};
const DEFAULT_SECONDARY_TEXT_COLOR = '#6c757d';

function getSecondaryTextColor(ride) {
    return SECONDARY_TEXT_COLORS[getRowStatusClass(ride)] || DEFAULT_SECONDARY_TEXT_COLOR;
}

const STACKED_SEPARATOR = ' | ';
const DANGER_TEXT_COLOR = '#dc3545';

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function renderStackedFilterLine(line, text) {
    if (!text) {
        return '';
    }
    const content = `${line.icon ? `<i class="fas ${line.icon}"></i> ` : ''}${escapeHtml(text)}`;
    if (line.badgeClass) {
        return `<div style="margin-top: 2px;"><span class="badge badge-pill ${line.badgeClass(text)}" style="font-size: 0.85em;">${content}</span></div>`;
    }
    const color = line.tone === 'danger' ? DANGER_TEXT_COLOR : line.tone === 'secondary' ? DEFAULT_SECONDARY_TEXT_COLOR : '';
    return `<div${color ? ` style="color: ${color};"` : ''}>${content}</div>`;
}

// Column props for a cell that stacks several values (each column still has its own cellRender
// for the grid itself). The calculated value joins every line, empty positions included, so:
//  - the header filter, grid search and export all see every value, and the header-filter
//    search matches any part of any line;
//  - customizeText can split it back and render the header-filter list item with the same
//    stacked layout as the cell (list items are rendered as HTML because encodeHtml={false}).
// lines: [{ get(ride) => string, icon?, tone?: 'secondary' | 'danger', badgeClass?(text) }]
function stackedColumnProps(sortField, lines) {
    return {
        dataType: 'string',
        calculateCellValue(ride) {
            const parts = lines.map(line => line.get(ride) || '');
            return parts.some(Boolean) ? parts.join(STACKED_SEPARATOR) : '';
        },
        calculateSortValue: sortField,
        encodeHtml: false,
        customizeText(cellInfo) {
            if (cellInfo.target !== 'headerFilter' || !cellInfo.valueText) {
                return cellInfo.valueText;
            }
            const parts = cellInfo.valueText.split(STACKED_SEPARATOR);
            // A separator inside the last value (e.g. a free-text reject reason) stays part of it.
            const lastIndex = lines.length - 1;
            parts[lastIndex] = parts.slice(lastIndex).join(STACKED_SEPARATOR);
            const html = lines.map((line, i) => renderStackedFilterLine(line, parts[i])).join('');
            return `<div style="white-space: normal; line-height: 1.3;">${html}</div>`;
        },
        allowHeaderFiltering: true,
        headerFilter: { allowSearch: true, searchMode: 'contains', width: 280, height: 400 },
    };
}

// "Cockpit Crew • FEMALE" — the Pax cell's second line (shared by the cell and its header filter).
function getCrewTypeAndGender(ride) {
    return [ride.crewType, ride.gender].filter(Boolean).join(' • ');
}

const PAX_COLUMN_PROPS = stackedColumnProps('paxName', [
    { get: r => r.paxName },
    { get: getCrewTypeAndGender, tone: 'secondary' },
]);

const ACCOUNT_COLUMN_PROPS = stackedColumnProps('serviceAccountName', [
    { get: r => r.serviceAccountName },
    { get: r => r.serviceLocationName, icon: 'fa-map-marker-alt', tone: 'secondary' },
]);

const DRIVER_COLUMN_PROPS = stackedColumnProps('driverName', [
    { get: r => r.driverName },
    { get: r => r.driverMobileNo, icon: 'fa-phone-alt', tone: 'secondary' },
]);

const GUARD_COLUMN_PROPS = stackedColumnProps('guardName', [
    { get: r => r.guardName },
    { get: r => r.guardMobileNo, icon: 'fa-phone-alt', tone: 'secondary' },
]);

const BOARDING_OTP_COLUMN_PROPS = stackedColumnProps('boardingOtp', [
    { get: r => r.boardingOtp || 'Not Assigned' },
    {
        get: r => (r.boardingOtp ? (r.isBoardingOtpUsed ? 'Used' : 'Not Used') : ''),
        badgeClass: text => (text === 'Used' ? 'badge-success' : 'badge-danger'),
    },
    { get: r => (r.boardingOtp && r.isBoardingOtpUsed ? formatCellDateTime(r.boardingOtpUsedDate) : ''), tone: 'secondary' },
]);

// Date-tree header filters (Activity, Assigned / Unassigned Time): group -> year -> month -> day -> time,
// so the operator can pick a group and narrow it by date the same way the Pickup Time filter works.
// Each node's value is a raw filter expression with a getter function; the grid applies array
// header-filter values as-is against the local rides array. The getters are created once (at module
// load) so their identity is stable, which the grid needs to show previously checked nodes as checked.
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
const pad2 = n => String(n).padStart(2, '0');

function parseDate(value) {
    if (!value) {
        return null;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
}

// Keys are zero-padded so the path strings sort chronologically.
const DATE_TREE_LEVELS = [
    { key: d => String(d.getFullYear()), text: d => String(d.getFullYear()) },
    { key: d => pad2(d.getMonth() + 1), text: d => MONTH_NAMES[d.getMonth()] },
    { key: d => pad2(d.getDate()), text: d => String(d.getDate()) },
    { key: d => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`, text: d => `${pad2(d.getHours())}:${pad2(d.getMinutes())}` },
];

function sortTreeNodes(nodes) {
    nodes.sort((a, b) => String(a.value[2]).localeCompare(String(b.value[2])));
    nodes.forEach(node => node.items && sortTreeNodes(node.items));
    return nodes;
}

// getGroup(ride) -> group text ('' shows as "(Blanks)"; null leaves the ride out of this tree).
// getDateValue(ride) -> the date under that group. Returns buildItems(rides) -> root nodes.
function createGroupedDateTree(getGroup, getDateValue) {
    // pathGetters[i](ride) -> "group/yyyy[/mm[/dd[/HH:mm]]]" down to level i.
    const pathGetters = DATE_TREE_LEVELS.map((_, levelIndex) => (ride) => {
        const group = getGroup(ride);
        const d = parseDate(getDateValue(ride));
        if (group === null || !d) {
            return null;
        }
        const keys = DATE_TREE_LEVELS.slice(0, levelIndex + 1).map(level => level.key(d));
        return [group, ...keys].join('/');
    });

    return function buildItems(rides) {
        const roots = [];
        const nodesByPath = new Map();
        rides.forEach((ride) => {
            const group = getGroup(ride);
            if (group === null) {
                return;
            }
            let parent = nodesByPath.get(group);
            if (!parent) {
                parent = { text: group || '(Blanks)', value: [getGroup, '=', group] };
                nodesByPath.set(group, parent);
                roots.push(parent);
            }
            const d = parseDate(getDateValue(ride));
            if (!d) {
                return;
            }
            let path = group;
            DATE_TREE_LEVELS.forEach((level, levelIndex) => {
                path = `${path}/${level.key(d)}`;
                let node = nodesByPath.get(path);
                if (!node) {
                    node = { text: level.text(d), value: [pathGetters[levelIndex], '=', path] };
                    nodesByPath.set(path, node);
                    parent.items = parent.items || [];
                    parent.items.push(node);
                }
                parent = node;
            });
        });
        return sortTreeNodes(roots);
    };
}

const buildActivityHeaderFilterItems = createGroupedDateTree(r => r.activityDisplay || '', r => r.activityTimeDisplay);

// Assigned / Unassigned Time: "Assigned" and "Unassigned" each open into their own date tree, then
// "Reject Reason" lists each reason, then "(Blanks)" for rides with none of the three.
const buildAssignedTimeTree = createGroupedDateTree(r => (parseDate(r.assignedTime) ? 'Assigned' : null), r => r.assignedTime);
const buildUnassignedTimeTree = createGroupedDateTree(r => (parseDate(r.unAssignedTime) ? 'Unassigned' : null), r => r.unAssignedTime);
const getRejectReasonGroup = r => (r.rejectReson ? 'Reject Reason' : null);
const getRejectReason = r => r.rejectReson || null;
const hasNoAssignmentInfo = r => !parseDate(r.assignedTime) && !parseDate(r.unAssignedTime) && !r.rejectReson;

function buildAssignedUnassignedHeaderFilterItems(rides) {
    const items = [...buildAssignedTimeTree(rides), ...buildUnassignedTimeTree(rides)];
    const reasons = [...new Set(rides.map(getRejectReason).filter(Boolean))].sort();
    if (reasons.length) {
        items.push({
            text: 'Reject Reason',
            value: [getRejectReasonGroup, '=', 'Reject Reason'],
            items: reasons.map(reason => ({ text: reason, value: [getRejectReason, '=', reason] })),
        });
    }
    if (rides.some(hasNoAssignmentInfo)) {
        items.push({ text: '(Blanks)', value: [hasNoAssignmentInfo, '=', true] });
    }
    return items;
}

// Clubbed No comes back as 0 for rides that aren't clubbed; show those as empty instead.
function getClubbedNoText(ride) {
    const value = ride.clubbedNo;
    return value === null || value === undefined || String(value).trim() === '' || Number(value) === 0 ? '' : String(value);
}

// Splits a day into four fixed 6-hour pickup-time slots. Bounds are inclusive of seconds
// (e.g. slot 1 ends at 05:59:59, not 06:00:00) so filtering matches pickupDateTime down to
// the second, not just the hour.
// Labels show only the hour (no minutes/seconds); the actual filtering still matches
// pickupDateTime down to the second via startSeconds/endSeconds below.
const PICKUP_TIME_SLOTS = [
    { id: 1, label: '12 AM - 6 AM', startSeconds: 0 * 3600, endSeconds: 6 * 3600 - 1 },
    { id: 2, label: '6 AM - 12 PM', startSeconds: 6 * 3600, endSeconds: 12 * 3600 - 1 },
    { id: 3, label: '12 PM - 6 PM', startSeconds: 12 * 3600, endSeconds: 18 * 3600 - 1 },
    { id: 4, label: '6 PM - 12 AM', startSeconds: 18 * 3600, endSeconds: 24 * 3600 - 1 },
];

export class SgDispatchComponent extends Component {
    static displayName = SgDispatchComponent.name;

    constructor(props) {
        super(props);
        this.gridWrapperRef = React.createRef();
        const today = new Date();
        this.state = {
            rides: [],
            // filterPeriodLabel from the latest guard-rides payload, shown as-is in the toolbar badge.
            filterPeriodLabel: '',
            // True when the grid toolbar is too narrow for the "Showing rides for" badge, which then
            // moves next to the page title instead of overlapping the toolbar's guard controls.
            isFilterPeriodInHeader: false,
            loadPanelVisible: false,
            gridHeight: 500,
            gridWidth: '100%',
            securityGuards: [],
            selectedSecurityGuardId: null,
            selectedPickupTimeSlotId: null,
            isAssignGuardSubmitting: false,
            isUnassignGuardSubmitting: false,
            selectedRowKeys: [],
            hasAssignedGuardSelection: false,
            isSearchLoading: false,
            isShowDispatchFilter: true,
            gridSearchText: '',
            // Defaults to today's date range; sent to the guard-rides API as
            // startTime/endTime (see loadRides/toApiDate).
            searchFromDate: formatFromDate(today, "MM/dd/yyyy hh:mm"),
            searchToDate: formatToDate(today, "MM/dd/yyyy hh:mm"),
            isVisiblePickupSchedules: false,
            isShowPickupSchedules: false,
            isShowPending: false,
            isShowInProcess: false,
            isShowCompleted: false,
            isShowCancelled: false,
            isShowAll: true,
        };

        this.loadRides = this.loadRides.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleGridSearchChange = this.handleGridSearchChange.bind(this);
        this.handleGridSearchPaste = this.handleGridSearchPaste.bind(this);
        this.handleGridSearchClear = this.handleGridSearchClear.bind(this);
        this.handleSearchFromDateChange = this.handleSearchFromDateChange.bind(this);
        this.handleSearchToDateChange = this.handleSearchToDateChange.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.handleReset = this.handleReset.bind(this);
        this.handleRowPrepared = this.handleRowPrepared.bind(this);
        this.updateGridDimensions = this.updateGridDimensions.bind(this);
        this.handleSecurityGuardChange = this.handleSecurityGuardChange.bind(this);
        this.handlePickupTimeSlotChange = this.handlePickupTimeSlotChange.bind(this);
        this.handleAssignGuard = this.handleAssignGuard.bind(this);
        this.handleUnassignGuard = this.handleUnassignGuard.bind(this);
        this.handleSelectionChanged = this.handleSelectionChanged.bind(this);
        this.onCellPrepared = this.onCellPrepared.bind(this);
        this.onToolbarPreparing = this.onToolbarPreparing.bind(this);
        this.renderToolbarItems = this.renderToolbarItems.bind(this);
        this.renderExportItems = this.renderExportItems.bind(this);
        this.renderFilterPeriodBadge = this.renderFilterPeriodBadge.bind(this);
        // Toolbar width that was needed while the badge was still in it (see checkToolbarFit).
        this.toolbarWidthWithBadge = 0;
        this.renderActivityCell = this.renderActivityCell.bind(this);
        this.renderAccountCell = this.renderAccountCell.bind(this);
        this.renderPaxCell = this.renderPaxCell.bind(this);
        this.renderBoardingOtpCell = this.renderBoardingOtpCell.bind(this);
        this.renderDriverCell = this.renderDriverCell.bind(this);
        this.renderGuardCell = this.renderGuardCell.bind(this);
        this.renderAssignedUnassignedCell = this.renderAssignedUnassignedCell.bind(this);
    }

    async componentDidMount() {
        // Same viewport-fill approach as DispatchGrid.js, so this grid takes up the
        // remaining page height/width instead of collapsing to a short auto-sized table.
        this.updateGridDimensions();
        window.addEventListener('resize', this.updateGridDimensions);
        await this.loadRides();
        await this.loadSecurityGuards();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateGridDimensions);
    }

    updateGridDimensions() {
        if (!this.gridWrapperRef.current) {
            return;
        }
        const { top, width } = this.gridWrapperRef.current.getBoundingClientRect();
        this.setState({
            gridHeight: Math.max(300, window.innerHeight - top - 4),
            gridWidth: Math.max(280, width),
        }, this.checkToolbarFit);
    }

    // Moves the "Showing rides for" badge out of the grid toolbar (to the page header) when the
    // toolbar's left (guard controls) and right (badge + Export) sections would overlap, and back
    // once there's room again. Measures the real toolbar rather than using a screen-size breakpoint,
    // since the same laptop can be 1366-1920px wide depending on scaling and zoom. It only switches
    // back once the width needed with the badge is available again, so it can't flicker.
    checkToolbarFit = () => {
        window.requestAnimationFrame(() => {
            const grid = this.dataGrid && this.dataGrid.instance;
            const toolbar = grid && grid.element().querySelector('.dx-datagrid-header-panel .dx-toolbar');
            const before = toolbar && toolbar.querySelector('.dx-toolbar-before');
            const after = toolbar && toolbar.querySelector('.dx-toolbar-after');
            if (!before || !after) {
                return;
            }
            const available = toolbar.clientWidth;
            if (!this.state.isFilterPeriodInHeader) {
                if (!this.state.filterPeriodLabel) {
                    return;
                }
                const needed = before.scrollWidth + after.scrollWidth + 16;
                if (needed > available) {
                    this.toolbarWidthWithBadge = needed;
                    this.setState({ isFilterPeriodInHeader: true }, this.afterFilterPeriodMoved);
                }
            } else if (available >= this.toolbarWidthWithBadge) {
                this.setState({ isFilterPeriodInHeader: false }, this.afterFilterPeriodMoved);
            }
        });
    }

    // Rebuilds the toolbar template (with/without the badge) and re-fits the grid height, since the
    // page header grows or shrinks when the badge moves into or out of it.
    afterFilterPeriodMoved = () => {
        this.refreshFilterPeriodLabel();
        this.updateGridDimensions();
    }

    async loadRides() {
        this.setState({ loadPanelVisible: true });
        try {
            const startTime = `${toApiDate(this.state.searchFromDate)} 00:00:00`;
            const endTime = `${toApiDate(this.state.searchToDate)} 23:59:59`;
            const requestParams = getRequestParams('POST', { startTime, endTime });
            const response = await fetch('/dispatch-api/guard-rides', requestParams);
            const data = await response.json();

            if (data.isSuccess) {
                const rides = data.rides || [];
                // Taken fresh from every payload, exactly as the API sends it (see refreshFilterPeriodLabel).
                const filterPeriodLabel = rides.length > 0 && rides[0].filterPeriodLabel != null ? String(rides[0].filterPeriodLabel) : '';
                this.setState({ rides, filterPeriodLabel }, this.refreshFilterPeriodLabel);
            } else {
                this.setState({ filterPeriodLabel: '' }, this.refreshFilterPeriodLabel);
                await Swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: data.resultMessage,
                });
            }
        } catch (e) {
            this.setState({ filterPeriodLabel: '' }, this.refreshFilterPeriodLabel);
            await Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Something went wrong while loading guard-tracked rides.',
            });
        } finally {
            this.setState({ loadPanelVisible: false });
        }
    }

    // The label lives in a DevExtreme toolbar template ('sgExportItems'), which DevExtreme renders when
    // it builds the toolbar rather than on every React state change. Repainting the grid rebuilds the
    // toolbar, so the badge shows the value from the latest payload.
    refreshFilterPeriodLabel = () => {
        if (this.dataGrid && this.dataGrid.instance) {
            this.dataGrid.instance.repaint();
        }
    }

    async loadSecurityGuards() {
        try {
            const requestParams = getRequestParams('GET');
            const response = await fetch('account-api/security-guard-dropdown', requestParams);
            if (response.ok) {
                const data = await response.json();
                if (data.isSuccess) {
                    this.setState({ securityGuards: data.users || [] });
                }
            }
        } catch (e) {
            // Non-critical: leave the dropdown empty rather than blocking the page with an error.
        }
    }

    handleSecurityGuardChange(e) {
        this.setState({ selectedSecurityGuardId: e.selectedItem ? e.selectedItem.id : null });
    }

    handlePickupTimeSlotChange(e) {
        this.setState({ selectedPickupTimeSlotId: e.selectedItem ? e.selectedItem.id : null });
    }

    // Only Pending rides (tripStatusId in PENDING_STATUS_IDS) can be assigned a guard, so
    // those are the only rows selectable via the grid's row checkbox.
    isRideSelectable(ride) {
        return !!ride && PENDING_STATUS_IDS.includes(parseInt(ride.tripStatusId));
    }

    handleSelectionChanged(e) {
        const rideById = new Map(this.state.rides.map(r => [r.rideId, r]));
        let allowedKeys = e.selectedRowKeys.filter(key => this.isRideSelectable(rideById.get(key)));
        const previousKeys = new Set(this.state.selectedRowKeys);

        // Cap the selection at MAX_SELECTED_RIDES first, keeping the rides that were already selected
        // and dropping the newly checked ones that would push it over the limit. This check runs
        // before the account/location/ride type check so the limit warning always takes precedence.
        let isLimitReached = false;
        if (allowedKeys.length > MAX_SELECTED_RIDES) {
            allowedKeys = [
                ...allowedKeys.filter(key => previousKeys.has(key)),
                ...allowedKeys.filter(key => !previousKeys.has(key)),
            ].slice(0, MAX_SELECTED_RIDES);
            isLimitReached = true;
            Swal.fire({
                icon: 'warning',
                title: 'Selection limit reached',
                text: `You can select a maximum of ${MAX_SELECTED_RIDES} ${MAX_SELECTED_RIDES === 1 ? 'ride' : 'rides'} at a time. Please unselect a ride before selecting another one.`,
            });
        }

        // A batch assign carries a single AccountId/LocationId, so every selected ride must share
        // the account/location and ride type of the rides already selected (or of the first newly
        // checked ride when nothing was selected before). Newly checked rides that don't match are rejected.
        const anchorKey = allowedKeys.find(key => previousKeys.has(key)) ?? allowedKeys[0];
        const anchorRide = rideById.get(anchorKey);
        let rejectedRide = null;
        let rejectReason = null;
        if (anchorRide) {
            allowedKeys = allowedKeys.filter(key => {
                const ride = rideById.get(key);
                let reason = null;
                if (ride.serviceAccountId !== anchorRide.serviceAccountId
                    || ride.serviceLocationId !== anchorRide.serviceLocationId) {
                    reason = 'serviceLocation';
                } else if (ride.rideTypeId !== anchorRide.rideTypeId) {
                    reason = 'rideType';
                }
                if (reason && !rejectedRide) {
                    rejectedRide = ride;
                    rejectReason = reason;
                }
                return !reason;
            });
        }
        // Swal shows one popup at a time, so skip this warning if the limit warning was already shown.
        if (rejectedRide && !isLimitReached) {
            const escape = value => String(value || '-').replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`);
            if (rejectReason === 'serviceLocation') {
                const describe = ride => `${escape(ride.serviceAccountName)} / ${escape(ride.serviceLocationName)}`;
                Swal.fire({
                    icon: 'warning',
                    title: 'Different account/location',
                    html: `Ride <b>${escape(rejectedRide.rideId)}</b> belongs to <b>${describe(rejectedRide)}</b>, `
                        + `but your current selection is for <b>${describe(anchorRide)}</b>.<br/><br/>`
                        + 'You can only select rides from the same account and location at a time. Clear the current selection to pick rides from a different account/location.',
                });
            } else {
                const describe = ride => escape(ride.rideTypeName);
                Swal.fire({
                    icon: 'warning',
                    title: 'Different ride type',
                    html: `Ride <b>${escape(rejectedRide.rideId)}</b> is a <b>${describe(rejectedRide)}</b> ride, `
                        + `but your current selection is for <b>${describe(anchorRide)}</b> rides.<br/><br/>`
                        + 'You can only select rides of the same ride type at a time. Clear the current selection to pick rides of a different ride type.',
                });
            }
        }

        // Selecting all / clicking a disallowed row can still hand us keys we don't want
        // (e.g. "select all pages"); correct the grid's own selection back down to just
        // the allowed rows instead of trusting e.selectedRowKeys as-is.
        if (allowedKeys.length !== e.selectedRowKeys.length && this.dataGrid && this.dataGrid.instance) {
            this.dataGrid.instance.option('selectedRowKeys', allowedKeys);
            return;
        }

        const selectedKeys = new Set(allowedKeys);
        const selectedRides = this.state.rides.filter(r => selectedKeys.has(r.rideId));
        this.setState({
            selectedRowKeys: allowedKeys,
            // Drives whether the Unassign Guard button is enabled — only when the current
            // selection includes at least one ride that already has a guard assigned.
            hasAssignedGuardSelection: selectedRides.some(r => !!r.guardUserId),
        });
    }

    // Sets readable text on dark status rows, and replaces the row-selection checkbox with a lock
    // icon for non-Pending rows so it's visually clear they can't be checked (backed by the actual
    // enforcement in handleSelectionChanged).
    onCellPrepared(e) {
        if (!e.column) {
            return;
        }
        if (e.column.command !== 'select') {
            const rowTextColor = e.rowType === 'data' && ROW_TEXT_COLORS[getRowStatusClass(e.data)];
            if (rowTextColor) {
                e.cellElement.style.setProperty('color', rowTextColor, 'important');
            }
            return;
        }
        // Header "select all" checkbox is never wanted here — Pending-only selection makes
        // a blanket select-all meaningless, and allowSelectAll={false} alone still leaves the
        // checkbox rendered (just inert), so strip the header cell's content entirely instead.
        if (e.rowType === 'header') {
            e.cellElement.innerHTML = '';
            return;
        }
        // Status color classes are applied to the whole row in handleRowPrepared;
        // keep the checkbox column neutral so only the data cells show the status color.
        if (e.rowType === 'data') {
            e.cellElement.style.setProperty('background-color', '#ffffff', 'important');
        }
        if (e.rowType === 'data' && !this.isRideSelectable(e.data)) {
            // Swap the checkbox for a lock icon + tooltip so the operator can tell why the row
            // can't be picked (handleSelectionChanged still filters these out regardless).
            e.cellElement.innerHTML = '<i class="fas fa-lock" style="color: #adb5bd;"></i>';
            e.cellElement.title = 'Only Pending rides can be selected';
            e.cellElement.style.cursor = 'not-allowed';
        }
    }

    // GuardAssignRequest/GuardUnassignRequest carry a single AccountId/LocationId for the
    // whole batch, so those come from the selected ride(s) themselves (ServiceAccountId/
    // ServiceLocationId), not from the logged-in dispatcher's own account/location.
    // Returns null (after showing a warning) if the selection spans more than one account/location.
    async getSelectedRideServiceLocation(rideIds) {
        const selectedKeys = new Set(rideIds || this.state.selectedRowKeys);
        const selectedRides = this.state.rides.filter(r => selectedKeys.has(r.rideId));

        const accountIds = new Set(selectedRides.map(r => r.serviceAccountId));
        const locationIds = new Set(selectedRides.map(r => r.serviceLocationId));

        if (accountIds.size > 1 || locationIds.size > 1) {
            await Swal.fire({
                icon: 'warning',
                title: 'Mixed account/location',
                text: 'Please select rides that all belong to the same account and location.',
            });
            return null;
        }

        return {
            accountId: selectedRides[0]?.serviceAccountId,
            locationId: selectedRides[0]?.serviceLocationId,
        };
    }

    async handleAssignGuard() {
        if (!this.state.selectedSecurityGuardId) {
            await Swal.fire({
                icon: 'warning',
                title: 'Select a guard',
                text: 'Please select a security guard before assigning.',
            });
            return;
        }
        if (this.state.selectedRowKeys.length === 0) {
            await Swal.fire({
                icon: 'warning',
                title: 'Select ride(s)',
                text: 'Please select one or more rides to assign the guard to.',
            });
            return;
        }

        // Same guard as DispatchComponent's handleClubReservations: block the whole action
        // (rather than silently skipping) if any selected ride already has a guard assigned.
        const selectedKeys = new Set(this.state.selectedRowKeys);
        const selectedRides = this.state.rides.filter(r => selectedKeys.has(r.rideId));
        const alreadyAssigned = selectedRides.filter(r => !!r.guardUserId);
        if (alreadyAssigned.length > 0) {
            const rideIds = alreadyAssigned.map(r => r.rideId).join(', ');
            await Swal.fire({
                icon: 'warning',
                title: 'Guard Already Assigned',
                html: 'The following Ride Id(s) already have a guard assigned: <b>' + rideIds + '</b>. Please deselect them and try again.',
            });
            return;
        }

        const serviceLocation = await this.getSelectedRideServiceLocation();
        if (!serviceLocation) {
            return;
        }

        this.setState({ isAssignGuardSubmitting: true });
        try {
            const request = {
                AccountId: serviceLocation.accountId,
                LocationId: serviceLocation.locationId,
                RideIds: this.state.selectedRowKeys,
                GuardUserId: this.state.selectedSecurityGuardId,
                DeviceTime: new Date().toISOString(),
            };
            const requestParams = getRequestParams('POST', request);
            const response = await fetch('/dispatch-api/guard-assign', requestParams);
            const data = await response.json();

            await Swal.fire({
                icon: data.isSuccess ? 'success' : 'error',
                title: data.isSuccess ? 'Guard Assigned' : 'Oops...',
                text: data.resultMessage,
            });

            if (data.isSuccess) {
                this.setState({ selectedRowKeys: [], hasAssignedGuardSelection: false });
                await this.loadRides();
            }
        } catch (e) {
            await Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Something went wrong while assigning the guard.',
            });
        } finally {
            this.setState({ isAssignGuardSubmitting: false });
        }
    }

    async handleUnassignGuard() {
        if (this.state.selectedRowKeys.length === 0) {
            await Swal.fire({
                icon: 'warning',
                title: 'Select ride(s)',
                text: 'Please select one or more rides to unassign the guard from.',
            });
            return;
        }

        // Same pattern as DispatchComponent's handleUnclubReservations: only act on the
        // subset of the selection that actually has a guard assigned.
        const selectedKeys = new Set(this.state.selectedRowKeys);
        const selectedRides = this.state.rides.filter(r => selectedKeys.has(r.rideId));
        const assignedRides = selectedRides.filter(r => !!r.guardUserId);
        if (assignedRides.length === 0) {
            await Swal.fire({
                icon: 'info',
                title: 'Select ride(s)',
                text: 'Please select at least one ride that already has a guard assigned.',
            });
            return;
        }
        const assignedRideIds = assignedRides.map(r => r.rideId);

        const serviceLocation = await this.getSelectedRideServiceLocation(assignedRideIds);
        if (!serviceLocation) {
            return;
        }

        this.setState({ isUnassignGuardSubmitting: true });
        try {
            const request = {
                AccountId: serviceLocation.accountId,
                LocationId: serviceLocation.locationId,
                RideIds: assignedRideIds,
                DeviceTime: new Date().toISOString(),
            };
            const requestParams = getRequestParams('POST', request);
            const response = await fetch('/dispatch-api/guard-unassign', requestParams);
            const data = await response.json();

            await Swal.fire({
                icon: data.isSuccess ? 'success' : 'error',
                title: data.isSuccess ? 'Guard Unassigned' : 'Oops...',
                text: data.resultMessage,
            });

            if (data.isSuccess) {
                this.setState({ selectedRowKeys: [], hasAssignedGuardSelection: false });
                await this.loadRides();
            }
        } catch (e) {
            await Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Something went wrong while unassigning the guard.',
            });
        } finally {
            this.setState({ isUnassignGuardSubmitting: false });
        }
    }

    // Injects the security-guard dropdown + Assign Guard button into the DataGrid's own
    // toolbar (same mechanism DispatchComponent uses for its vehicle/driver dropdowns),
    // instead of a separate row above the grid.
    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift({
            location: 'before',
            template: 'sgToolbarItems',
        });
        // Replace the built-in export button with our own so the API's filterPeriodLabel
        // can be stacked directly underneath it.
        const exportIndex = e.toolbarOptions.items.findIndex(item => item.name === 'exportButton');
        if (exportIndex > -1) {
            e.toolbarOptions.items.splice(exportIndex, 1, {
                location: 'after',
                template: 'sgExportItems',
            });
        }
    }

    // "Showing rides for [filterPeriodLabel]" — shown in the grid toolbar, or in the page header when
    // the toolbar is too narrow (allowWrap lets it break onto two lines there).
    renderFilterPeriodBadge(allowWrap) {
        const { filterPeriodLabel } = this.state;
        if (!filterPeriodLabel) {
            return null;
        }
        return (
            <>
                <label className="mr-2 mb-0" style={{ fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>Showing rides for</label>
                <span
                    className="badge badge-pill badge-info"
                    title="Only rides within this time window are loaded in the grid"
                    style={{ marginRight: 8, padding: '5px 14px', fontSize: '0.95rem', lineHeight: 1.4, whiteSpace: allowWrap ? 'normal' : 'nowrap', textAlign: 'left', fontWeight: 600, letterSpacing: '0.3px' }}
                >
                    {/* filterPeriodLabel is shown exactly as the API returns it (no date/time formatting);
                        white-space: pre keeps its spacing instead of HTML collapsing it. */}
                    <i className="fas fa-calendar-alt"></i> <span style={{ whiteSpace: allowWrap ? 'pre-wrap' : 'pre' }}>{filterPeriodLabel}</span>
                </span>
            </>
        );
    }

    renderExportItems() {
        return (
            <div className="d-flex align-items-center">
                {!this.state.isFilterPeriodInHeader && this.renderFilterPeriodBadge(false)}
                <button type="button" className="btn btn-outline-secondary btn-sm" title="Export to Excel" onClick={() => this.dataGrid && this.dataGrid.instance && this.dataGrid.instance.exportToExcel(false)}>
                    <i className="fas fa-file-excel"></i> Export
                </button>
            </div>
        );
    }

    // Stacks Assigned/Unassigned time with the reject reason (only present when a driver
    // rejected the trip) so the three related fields read as one grouped cell.
    renderAssignedUnassignedCell(cellInfo) {
        const r = cellInfo.data;
        return (
            <div>
                {r.assignedTime && <div>Assigned: {formatCellDateTime(r.assignedTime)}</div>}
                {r.unAssignedTime && <div>Unassigned: {formatCellDateTime(r.unAssignedTime)}</div>}
                {r.rejectReson && <div style={{ color: '#dc3545', fontSize: '0.85em' }}>Reject Reason: {r.rejectReson}</div>}
            </div>
        );
    }

    // Shows activityDisplay with its activityTimeDisplay stacked underneath.
    renderActivityCell(cellInfo) {
        const r = cellInfo.data;
        return (
            <div>
                <div>{r.activityDisplay}</div>
                {r.activityTimeDisplay && <div style={{ color: getSecondaryTextColor(r), fontSize: '0.85em' }}><i className="fas fa-clock"></i> {formatCellDateTime(r.activityTimeDisplay)}</div>}
            </div>
        );
    }

    renderAccountCell(cellInfo) {
        const r = cellInfo.data;
        return (
            <div>
                <div>{r.serviceAccountName}</div>
                {r.serviceLocationName && <div style={{ color: getSecondaryTextColor(r), fontSize: '1em' }}><i className="fas fa-map-marker-alt"></i> {r.serviceLocationName}</div>}
            </div>
        );
    }

    // Shows paxName with its crewType and gender on one line underneath.
    renderPaxCell(cellInfo) {
        const r = cellInfo.data;
        const crewTypeAndGender = getCrewTypeAndGender(r);
        return (
            <div>
                <div>{r.paxName}</div>
                {crewTypeAndGender && <div style={{ color: getSecondaryTextColor(r), fontSize: '0.85em' }}>{crewTypeAndGender}</div>}
            </div>
        );
    }

    // Shows driverName with the driver's mobile number stacked underneath.
    renderDriverCell(cellInfo) {
        const r = cellInfo.data;
        return (
            <div>
                <div>{r.driverName}</div>
                {r.driverMobileNo && <div style={{ color: getSecondaryTextColor(r), fontSize: '1em' }}><i className="fas fa-phone-alt"></i> {r.driverMobileNo}</div>}
            </div>
        );
    }

    // Shows guardName with the guard's mobile number stacked underneath.
    renderGuardCell(cellInfo) {
        const r = cellInfo.data;
        return (
            <div>
                <div>{r.guardName}</div>
                {r.guardMobileNo && <div style={{ color: getSecondaryTextColor(r), fontSize: '1em' }}><i className="fas fa-phone-alt"></i> {r.guardMobileNo}</div>}
            </div>
        );
    }

    // Stacks the Boarding OTP with its used-flag/used-date so the three related fields
    // read as one grouped cell.
    renderBoardingOtpCell(cellInfo) {
        const r = cellInfo.data;
        if (!r.boardingOtp) {
            return <div>Not Assigned</div>;
        }
        return (
            <div>
                <div>{r.boardingOtp}</div>
                <div style={{ marginTop: 2 }}>
                    <span className={`badge badge-pill ${r.isBoardingOtpUsed ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: '0.85em' }}>
                        {r.isBoardingOtpUsed ? 'Used' : 'Not Used'}
                    </span>
                    {r.isBoardingOtpUsed && r.boardingOtpUsedDate &&
                        <span style={{ color: getSecondaryTextColor(r), fontSize: '0.9em', marginLeft: 4 }}>{formatCellDateTime(r.boardingOtpUsedDate)}</span>}
                </div>
            </div>
        );
    }

    renderToolbarItems() {
        return (
            <div className="d-flex align-items-center">
                <label htmlFor="sg-security-guard-dropdown" className="mr-2 mb-0" style={{ fontWeight: 600 }}>Security Guard</label>
                <SelectBox
                    id="sg-security-guard-dropdown"
                    dataSource={this.state.securityGuards}
                    value={this.state.selectedSecurityGuardId}
                    displayExpr="name"
                    valueExpr="id"
                    placeholder="Select Security Guard"
                    showClearButton={true}
                    searchEnabled={true}
                    searchExpr="name"
                    searchMode="contains"
                    width={250}
                    onSelectionChanged={this.handleSecurityGuardChange}
                />
                &nbsp;
                <button type="button" id="btnAssignGuard" className="btn btn-success btn-sm" title="Assign Guard" disabled={this.state.selectedRowKeys.length === 0} onClick={this.handleAssignGuard}>
                    {this.state.isAssignGuardSubmitting && <span><i className="fas fa-spinner fa-spin"></i> In-process</span>}
                    {!this.state.isAssignGuardSubmitting && <span><i className="fas fa-user-shield"></i> Assign Guard</span>}
                </button>
                &nbsp;
                <button type="button" id="btnUnassignGuard" className="btn btn-danger btn-sm" title="Unassign Guard" disabled={!this.state.hasAssignedGuardSelection} onClick={this.handleUnassignGuard}>
                    {this.state.isUnassignGuardSubmitting && <span><i className="fas fa-spinner fa-spin"></i> In-process</span>}
                    {!this.state.isUnassignGuardSubmitting && <span><i className="fas fa-user-slash"></i> Unassign Guard</span>}
                </button>
                &nbsp;
                <label htmlFor="sg-pickup-timeslot-dropdown" className="mr-2 mb-0" style={{ fontWeight: 600 }}>Pickup Time</label>
                <SelectBox
                    id="sg-pickup-timeslot-dropdown"
                    dataSource={PICKUP_TIME_SLOTS}
                    value={this.state.selectedPickupTimeSlotId}
                    displayExpr="label"
                    valueExpr="id"
                    placeholder="Filter by Pickup Time"
                    showClearButton={true}
                    width={220}
                    onSelectionChanged={this.handlePickupTimeSlotChange}
                />
                <span className="tab-badge tab-badge-info" title="Filtered record count">{this.getFilteredRides().length}</span>
            </div>
        );
    }

    // Client-side equivalent of DispatchComponent's _buildDispatchRequest status grouping.
    // Date range is enforced server-side via loadRides' startTime/endTime; only Ride Status
    // is applied here, against whatever rows the API already returned.
    getFilteredRides() {
        let rows = this.state.rides;

        if (!this.state.isShowAll) {
            const statusIds = new Set();
            if (this.state.isShowPending) PENDING_STATUS_IDS.forEach(id => statusIds.add(id));
            if (this.state.isShowInProcess) IN_PROCESS_STATUS_IDS.forEach(id => statusIds.add(id));
            if (this.state.isShowCompleted) COMPLETED_STATUS_IDS.forEach(id => statusIds.add(id));
            if (this.state.isShowCancelled) CANCELLED_STATUS_IDS.forEach(id => statusIds.add(id));
            if (statusIds.size > 0) {
                rows = rows.filter(r => statusIds.has(parseInt(r.tripStatusId)));
            }
        }

        if (this.state.selectedPickupTimeSlotId) {
            const slot = PICKUP_TIME_SLOTS.find(s => s.id === this.state.selectedPickupTimeSlotId);
            if (slot) {
                rows = rows.filter(r => {
                    if (!r.pickupDateTime) {
                        return false;
                    }
                    const d = new Date(r.pickupDateTime);
                    const seconds = d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
                    return seconds >= slot.startSeconds && seconds <= slot.endSeconds;
                });
            }
        }

        return rows;
    }

    handleInputChange(event) {
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;

        const rideStatusFields = ['isShowPending', 'isShowInProcess', 'isShowCompleted', 'isShowCancelled'];

        if (name === 'isShowAll' && value === true) {
            const cleared = {};
            rideStatusFields.forEach(f => { cleared[f] = false; });
            this.setState({ isShowAll: true, ...cleared });
            return;
        }

        if (rideStatusFields.includes(name) && value === true) {
            this.setState({ [name]: value, isShowAll: false });
            return;
        }

        this.setState({ [name]: value });
    }

    handleGridSearchChange(e) {
        const text = e.target.value;
        this.setState({ gridSearchText: text });
        if (this.dataGrid && this.dataGrid.instance) {
            this.dataGrid.instance.option('searchPanel.text', text);
        }
    }

    handleGridSearchPaste(e) {
        const pasted = (e.clipboardData || window.clipboardData).getData('text');
        if (!pasted) {
            return;
        }
        const el = e.target;
        const start = typeof el.selectionStart === 'number' ? el.selectionStart : el.value.length;
        const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : el.value.length;
        const current = this.state.gridSearchText || '';
        const newValue = current.slice(0, start) + pasted + current.slice(end);
        e.preventDefault();
        this.setState({ gridSearchText: newValue });
        if (this.dataGrid && this.dataGrid.instance) {
            this.dataGrid.instance.option('searchPanel.text', newValue);
        }
    }

    handleGridSearchClear() {
        if (this.dataGrid && this.dataGrid.instance) {
            this.dataGrid.instance.option('searchPanel.text', '');
        }
        this.setState({ gridSearchText: '' });
    }

    handleSearchFromDateChange(date) {
        this.setState({ searchFromDate: formatFromDate(date.value, "MM/dd/yyyy hh:mm") });
    }

    handleSearchToDateChange(date) {
        this.setState({ searchToDate: formatToDate(date.value, "MM/dd/yyyy hh:mm") });
    }

    async handleSearch(e) {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        this.setState({ isSearchLoading: true });
        await this.loadRides();
        this.setState({ isSearchLoading: false });
    }

    async handleReset() {
        this.handleGridSearchClear();
        const today = new Date();
        this.setState({
            searchFromDate: formatFromDate(today, "MM/dd/yyyy hh:mm"),
            searchToDate: formatToDate(today, "MM/dd/yyyy hh:mm"),
            isShowPending: false,
            isShowInProcess: false,
            isShowCompleted: false,
            isShowCancelled: false,
            isShowAll: true,
        });
        await this.loadRides();
    }

    // Colors the row by status so a guard-tracked ride reads the same way here as it would
    // in Dispatch List (see getRowStatusClass).
    handleRowPrepared(e) {
        if (e.rowType !== 'data') {
            return;
        }
        const statusClass = getRowStatusClass(e.data);
        if (statusClass) {
            e.rowElement.classList.add(statusClass);
        }
    }

    render() {
        const filteredRides = this.getFilteredRides();

        return (
            <div className="content-wrapper" id="dispatch-page">
                <div className="content-header">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6">
                                <h1 className="m-0 text-dark">SG Dispatch</h1>
                            </div>
                            {/* Holds the "Showing rides for" badge only when the grid toolbar is too narrow for it (checkToolbarFit). */}
                            <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 d-flex justify-content-end align-items-center flex-wrap">
                                {this.state.isFilterPeriodInHeader && this.renderFilterPeriodBadge(true)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content">
                    <ul className="nav nav-tabs dispatch-nav-tabs" id="custom-content-below-tab" role="tablist">
                        <li className="nav-item">
                            <a className="nav-link active" id="sgdispatcher-tab" data-toggle="pill" href="#dispatcher" role="tab" aria-controls="dispatcher" aria-selected="true">
                                <i className="fas fa-taxi"></i> Dispatch
                                {this.state.rides.length > 0 &&
                                    <span className="tab-badge tab-badge-info">{this.state.rides.length}</span>
                                }
                            </a>
                        </li>
                        <DispatchFilterBar
                            isShowDispatchFilter={this.state.isShowDispatchFilter}
                            searchFromDate={this.state.searchFromDate}
                            searchToDate={this.state.searchToDate}
                            isVisiblePickupSchedules={this.state.isVisiblePickupSchedules}
                            isShowPickupSchedules={this.state.isShowPickupSchedules}
                            isShowPending={this.state.isShowPending}
                            isShowInProcess={this.state.isShowInProcess}
                            isShowCompleted={this.state.isShowCompleted}
                            isShowCancelled={this.state.isShowCancelled}
                            isShowAll={this.state.isShowAll}
                            isSearchLoading={this.state.isSearchLoading}
                            gridSearchText={this.state.gridSearchText}
                            onGridSearchChange={this.handleGridSearchChange}
                            onGridSearchPaste={this.handleGridSearchPaste}
                            onGridSearchClear={this.handleGridSearchClear}
                            onInputChange={this.handleInputChange}
                            onFromDateChange={this.handleSearchFromDateChange}
                            onToDateChange={this.handleSearchToDateChange}
                            onSearch={this.handleSearch}
                            onReset={this.handleReset}
                        />
                    </ul>
                    <div className="tab-content" id="custom-content-below-tabContent" style={{ marginTop: 0 }}>
                        <div className="tab-pane fade show active" id="dispatcher" role="tabpanel" aria-labelledby="sgdispatcher-tab">
                            <div className="content">
                                <div className="card card-grid">
                                    <div className="card-body">
                                        <div ref={this.gridWrapperRef} style={{ flex: '1 1 auto', minHeight: 0 }}>
                                            <DataGrid
                                                ref={ref => this.dataGrid = ref}
                                                id="dispatch-grid-container-custom"
                                                dataSource={filteredRides}
                                                keyExpr="rideId"
                                                className="dx-datagrid-height"
                                                height={this.state.gridHeight}
                                                width={this.state.gridWidth}
                                                columnsAutoWidth={false}
                                                showColumnLines={true}
                                                showRowLines={true}
                                                showBorders={true}
                                                onToolbarPreparing={this.onToolbarPreparing}
                                                onContentReady={this.checkToolbarFit}
                                                rowAlternationEnabled={false}
                                                allowColumnResizing={true}
                                                columnResizingMode="widget"
                                                onRowPrepared={this.handleRowPrepared}
                                                onCellPrepared={this.onCellPrepared}
                                                selectedRowKeys={this.state.selectedRowKeys}
                                                onSelectionChanged={this.handleSelectionChanged}
                                            >
                                                <Template name="sgToolbarItems" render={this.renderToolbarItems} />
                                                <Template name="sgExportItems" render={this.renderExportItems} />
                                                <Selection mode="multiple" selectAllMode="allPages" showCheckBoxesMode="always" allowSelectAll={false} />
                                                <Scrolling mode="virtual" rowRenderingMode="virtual" showScrollbar="always" />
                                                <Paging enabled={false} />
                                                <SearchPanel visible={false} />
                                                <HeaderFilter visible={true} />
                                                <Export enabled={true} allowExportSelectedData={false} />

                                                <Column dataField="rideId" caption="Ride Id" width="auto" dataType="string" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                {/* Bound to the activity time so the header filter renders as a tree (like Pickup Time);
                                                    the tree itself is activity name -> year -> month -> day -> time (buildActivityHeaderFilterItems). */}
                                                <Column dataField="activityTimeDisplay" caption="Activity" width="auto" dataType="datetime" format={DateFormat.DateTime24Format} allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true, searchMode: 'contains', width: 280, height: 400, dataSource: buildActivityHeaderFilterItems(filteredRides) }} cellRender={this.renderActivityCell} />
                                                {/* Hidden so the grid search box still finds rides by activity name (search skips the date column above for non-date text). */}
                                                <Column dataField="activityDisplay" caption="Activity Name" visible={false} showInColumnChooser={false} allowSearch={true} />
                                                <Column dataField="pickupDateTime" caption="Pickup Time" width="auto" minWidth="130" dataType="datetime" format={DateFormat.DateTime24Format} allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                <Column name="guard" caption="Guard Name" width="auto" minWidth="150" {...GUARD_COLUMN_PROPS} allowFiltering={true} allowSearch={true} cellRender={this.renderGuardCell} />
                                                <Column name="pax" caption="Pax" width="auto" minWidth="120" {...PAX_COLUMN_PROPS} allowFiltering={true} allowSearch={true} cellRender={this.renderPaxCell} />
                                                <Column dataField="gender" caption="Gender" width="auto" minWidth="80" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                <Column dataField="crewType" caption="Crew Type" width="auto" minWidth="120" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                <Column name="account" caption="Account" width="auto" minWidth="120" {...ACCOUNT_COLUMN_PROPS} allowFiltering={true} allowSearch={true} cellRender={this.renderAccountCell} />
                                                <Column name="driver" caption="Driver" width="auto" minWidth="150" {...DRIVER_COLUMN_PROPS} allowFiltering={true} allowSearch={true} cellRender={this.renderDriverCell} />
                                                <Column dataField="vehicleNo" caption="Vehicle No" dataType="string" width="auto" minWidth="110" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true, searchMode: 'contains' }} />
                                                <Column dataField="rideTypeName" caption="Trip type" width="auto" minWidth="120" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                <Column dataField="tripStatus" caption="Status" width="auto" minWidth="120" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                <Column dataField="pickupLocation" caption="Pickup Location" width="300" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                <Column dataField="dropoffLocation" caption="Dropoff Location" width="300" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />                                                
                                                <Column name="clubbedNo" caption="Clubbed No" dataType="string" width="120" calculateCellValue={getClubbedNoText} calculateSortValue="clubbedNo" allowFiltering={true} allowHeaderFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true, searchMode: 'contains' }} />
                                                {/*<Column name="boardingOtpDetails" caption="Boarding OTP" width="160" {...BOARDING_OTP_COLUMN_PROPS} allowFiltering={true} allowSearch={true} cellRender={this.renderBoardingOtpCell} />*/}
                                                {/* Bound to assignedTime so the header filter renders as a tree; the tree itself is
                                                    Assigned/Unassigned -> year -> month -> day -> time, plus Reject Reason (buildAssignedUnassignedHeaderFilterItems). */}
                                                <Column dataField="assignedTime" caption="Assigned / Unassigned Time" width="220" dataType="datetime" format={DateFormat.DateTime24Format} allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true, searchMode: 'contains', width: 280, height: 400, dataSource: buildAssignedUnassignedHeaderFilterItems(filteredRides) }} cellRender={this.renderAssignedUnassignedCell} />
                                                {/* Hidden so the grid search box still finds rides by reject reason (search skips the date column above for non-date text). */}
                                                <Column dataField="rejectReson" caption="Reject Reason" visible={false} showInColumnChooser={false} allowSearch={true} />
                                                <Column dataField="locationTime" caption="Location Time" width="auto" minWidth="130" dataType="datetime" format={DateFormat.DateTime24Format} allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                <Column dataField="boardingTime" caption="Boarding Time" width="auto" minWidth="130" dataType="datetime" format={DateFormat.DateTime24Format} allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                                <Column dataField="completedTime" caption="Completed Time" width="auto" minWidth="130" dataType="datetime" format={DateFormat.DateTime24Format} allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                                            </DataGrid>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <LoadPanel
                    shadingColor="rgba(0,0,0,0.4)"
                    position={position}
                    visible={this.state.loadPanelVisible}
                    showIndicator={true}
                    shading={true}
                    showPane={true}
                    closeOnOutsideClick={false}
                />
            </div>
        );
    }
}
